import { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleContents, type ArticleSection } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cssbuyspreadsheet.cc';

function buildArticleJsonLd(article: typeof articleContents[string]) {
  const articleUrl = `${siteUrl}/artigo/${article.slug}`;
  const faqSections = article.sections.filter((s) => s.type === 'faq' && 'items' in s);

  const graph: Record<string, unknown>[] = [
    {
      '@type': 'Article',
      '@id': `${articleUrl}/#article`,
      headline: article.title,
      description: article.excerpt,
      image: article.heroImage,
      datePublished: '2026-06-01',
      dateModified: '2026-06-17',
      author: {
        '@type': 'Organization',
        name: 'CSSBuy Spreadsheet',
        url: siteUrl,
      },
      publisher: {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: 'CSSBuy Spreadsheet',
        url: siteUrl,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': articleUrl,
      },
      inLanguage: 'pt-PT',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'CSSBuy Spreadsheet',
          item: siteUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: article.title,
          item: articleUrl,
        },
      ],
    },
  ];

  if (faqSections.length > 0 && 'items' in faqSections[0]) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: faqSections[0].items.map((faq: { question: string; answer: string }) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

function renderSection(section: ArticleSection, index: number) {
  switch (section.type) {
    case 'paragraph':
      return (
        <p
          key={index}
          className="text-foreground-700 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: section.text }}
        />
      );
    case 'heading':
      return (
        <h2 key={index} className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-10 mb-4">
          {section.text}
        </h2>
      );
    case 'list':
      return section.ordered ? (
        <ol key={index} className="list-decimal pl-6 space-y-3 text-foreground-700 leading-relaxed my-4">
          {section.items.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </ol>
      ) : (
        <ul key={index} className="list-disc pl-6 space-y-3 text-foreground-700 leading-relaxed my-4">
          {section.items.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </ul>
      );
    case 'table':
      return (
        <div key={index} className="overflow-x-auto my-8">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-foreground-200">
                {section.headers.map((h, i) => (
                  <th
                    key={i}
                    className={`text-left py-3 px-4 font-semibold ${
                      i === 0 ? 'text-foreground-950' : i === 1 ? 'text-primary-600' : 'text-foreground-600'
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row, ri) => (
                <tr key={ri} className="border-b border-background-200/70">
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className={`py-3 px-4 ${
                        ci === 0
                          ? 'font-medium text-foreground-900'
                          : ci === 1
                          ? 'text-primary-600 font-semibold'
                          : 'text-foreground-600'
                      }`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'faq':
      return (
        <div key={index} className="space-y-4 my-6">
          {section.items.map((faq, fi) => (
            <div key={fi} className="bg-background-50 rounded-2xl p-5 border border-background-200/70">
              <h3 className="font-semibold text-foreground-950 mb-2">{faq.question}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
            </div>
          ))}
        </div>
      );
    case 'warning':
      return (
        <div key={index} className="bg-accent-50 border border-accent-200/60 rounded-2xl p-6 my-8">
          <div className="flex items-start gap-3">
            <i className="ri-error-warning-line text-accent-600 text-lg w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
            <p className="text-sm text-foreground-700 leading-relaxed">
              <strong className="text-accent-700">Aviso de Segurança:</strong> {section.text}
            </p>
          </div>
        </div>
      );
    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!slug || !articleContents[slug]) {
    return <Navigate to="/" replace />;
  }

  const article = articleContents[slug];
  const articleJsonLd = buildArticleJsonLd(article);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <Navbar />
      <main className="pt-24 md:pt-28">
        <article className="px-4 md:px-6 lg:px-12 py-8 md:py-12">
          <div className="max-w-3xl mx-auto">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors mb-6 cursor-pointer"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Voltar para CSSBuy Spreadsheet
            </Link>

            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-600 bg-accent-100/80 px-3 py-1.5 rounded-full mb-4">
              SEO Article
            </span>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-6">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500 mb-8">
              <span>Atualizado em {article.date}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>{article.readTime} de leitura</span>
            </div>

            <div className="rounded-3xl overflow-hidden h-64 md:h-96 bg-background-200 mb-10">
              <img
                src={article.heroImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-6">
              {article.sections.map((section, index) => renderSection(section, index))}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70 flex flex-col sm:flex-row items-center gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                Voltar para CSSBuy Spreadsheet
              </Link>
            </div>
          </div>
        </article>

        <section className="py-16 px-4 md:px-6 lg:px-12 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-6">
              Pronto para Começar?
            </h2>
            <p className="text-base text-foreground-600 mb-8">
              Aceda agora ao site principal e descubra milhares de produtos com o CSSBuy Spreadsheet.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-primary-500 text-background-50 rounded-full text-base font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                CSSBuy Spreadsheet
                <i className="ri-arrow-right-line text-xl w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform"></i>
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-foreground-950 text-background-50 rounded-full text-base font-semibold hover:bg-foreground-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <i className="ri-arrow-right-line text-xl w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform"></i>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}